import { useSelector } from "react-redux";
import { useClosePositionsMutation } from "../../../redux/exchangeUniApi";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { formatDecimal } from "../../../util/decimalFormatter";

const PositionTable = ({ positions = [], onEdit, manageScreenSize }) => {
  const [closePosition, { isLoading }] = useClosePositionsMutation();
  const livePrices = useSelector((state) => state.market.prices);
  const [closingIds, setClosingIds] = useState(new Set());

  useEffect(() => {
    setClosingIds((prev) => {
      const activeIds = new Set(positions.map((pos) => pos.id));
      const cleanedSet = new Set(
        Array.from(prev).filter((id) => activeIds.has(id))
      );
      if (prev.size !== cleanedSet.size) {
        console.log(
          "Auto-cleaned closed position IDs from loading:",
          [...prev].filter((id) => !activeIds.has(id))
        );
      }
      return cleanedSet;
    });
  }, [positions]);

  const handleClosePositions = async (positionId) => {
    if (closingIds.has(positionId)) return;

    const newSet = new Set(closingIds);
    newSet.add(positionId);
    setClosingIds(newSet);

    try {
      await closePosition({ position_id: positionId }).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to close position");
    } finally {
      const updatedSet = new Set(closingIds);
      updatedSet.delete(positionId);
      setClosingIds(updatedSet);
    }
  };

  const calculatePNL = (pos) => {
    const priceObj = livePrices[pos.symbol?.toUpperCase()];
    const currentPrice = pos.side === "long" ? priceObj?.bid : priceObj?.ask;

    if (!currentPrice) return { pnl: "-", change: "-", gross: "-" };

    const entry = parseFloat(pos.entry_price);
    const qty = parseFloat(pos.quantity);
    const lev = parseFloat(pos.leverage || 1);
    const symbol = pos.symbol?.toUpperCase();
    const segment = pos.segment || "crypto";
    const side = pos.side;

    const direction = side === "long" ? 1 : -1;
    let pnlRaw = 0;

    if (segment === "forex") {
      const isJPY = symbol.endsWith("JPY");
      const isMetal = symbol.startsWith("XAU") || symbol.startsWith("XAG");
      const lots = qty;

      if (isMetal) {
        pnlRaw = (currentPrice - entry) * 100 * lots;
      } else if (isJPY) {
        pnlRaw = ((currentPrice - entry) * lots * 100000) / currentPrice;
      } else {
        pnlRaw = (currentPrice - entry) * 100000 * lots;
      }
    } else {
      pnlRaw =
        side === "long"
          ? (currentPrice - entry) * qty
          : (entry - currentPrice) * qty;
    }

    const fee = pos.fee || 0;
    const pnl = (pnlRaw.toFixed(2) - parseFloat(fee)).toFixed(2);
    const priceDiff = currentPrice - entry;
    const change = ((priceDiff / entry) * 100).toFixed(2);
    const gross = parseFloat(pnlRaw).toFixed(2);

    return { pnl, change, gross };
  };

  return (
    <div
      className={
        manageScreenSize === "tv_chart_container2-zero"
          ? "trading_table-f"
          : "trading_table mt-3"
      }
    >
      <table className="table mb-0">
        <thead>
          <tr>
            <th>Pair</th>
            <th>Direction</th>
            <th>Volume</th>
            <th>Entry</th>
            <th>Change</th>
            <th>TP</th>
            <th>SL</th>
            <th>Comm</th>
            <th>Open Time</th>
            <th>Gross</th>
            <th>P&L</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {positions.map((pos, i) => {
            const { pnl, change, gross } = calculatePNL(pos);
            const live = livePrices[pos.symbol?.toUpperCase()];
            const livePrice = pos.side === "long" ? live?.bid : live?.ask;

            return (
              <tr key={i}>
                <td>{pos.symbol}</td>
                <td style={{ color: pos.side === "long" ? "green" : "red" }}>
                  {pos.side}
                </td>
                <td>{pos.quantity}</td>
                <td>{formatDecimal(pos.entry_price)}</td>
                <td>
                  {livePrice ? formatDecimal(parseFloat(livePrice)) : "-"}
                </td>
                <td>
                  {pos.take_profit ? formatDecimal(pos.take_profit) : "-"}
                </td>
                <td>{pos.stop_loss ? formatDecimal(pos.stop_loss) : "-"}</td>
                <td>{pos.fee ? formatDecimal(pos.fee) : "-"}</td>
                <td>{moment(pos.created_at).format("lll")}</td>
                <td style={{ color: gross >= 0 ? "green" : "red" }}>
                  {formatDecimal(gross)}
                </td>
                <td style={{ color: pnl >= 0 ? "green" : "red" }}>
                  {formatDecimal(pnl)}
                </td>
                <td>
                  <i
                    className="bi bi-pencil-square mr-2 cursor-pointer"
                    onClick={() => onEdit(pos)}
                  />
                  {closingIds.has(pos.id) ? (
                    <span
                      className="loading-spin"
                      style={{
                        color: "rgb(0, 188, 139)",
                        cursor: "not-allowed",
                      }}
                    >
                      <div
                        className="loader"
                        style={{ display: "inline-block" }}
                      ></div>
                    </span>
                  ) : (
                    <i
                      className="bi bi-x-circle cursor-pointer"
                      onClick={() => handleClosePositions(pos.id)}
                      title="Close Position"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PositionTable;
